/**
 * Project-wide constants. Single source of truth so the magic numbers in
 * PRD §3 / §7 / §9 don't get spread across scene code.
 */

/** Native render resolution (PRD §7 Art Direction). */
export const NATIVE_WIDTH = 384;
export const NATIVE_HEIGHT = 216;

/** Tile grid (PRD §7 + ui-sketches §3). */
export const TILE_SIZE = 16;

/** UGC affordance cyan (PRD §3). Not used in Variant A. */
export const UGC_CYAN = 0x7dd9f5;

/**
 * Placeholder palette while real pixel-art is unblocked. These map to the
 * Visual Brief biome accents loosely; chosen so the play-stage hero stays the
 * brightest patch on screen (PRD §7).
 */
export const PALETTE = {
  sky: 0x223850,
  skyFar: 0x182a3e,
  ruinsMid: 0x344755,
  ground: 0x5a4634,
  groundTop: 0x76583d,
  /** Mossy stone tones used by the redesigned ground tile (PRD-aligned forest biome). */
  stoneA: 0x3a3a3a,
  stoneB: 0x4d4d4d,
  stoneC: 0x5e5e5e,
  stoneMoss: 0x3c5a3a,
  stoneMossHilite: 0x6a8a52,
  hero: 0xf5efe0,
  heroOutline: 0xffffff,
  gegner: 0xa84438,
  geo: 0xf2c14e,
  hudPanel: 0x1a1018,
  hudText: 0xe2dccd,
  hudDim: 0x6e5c4f,
  hp: 0xcb4f4f,
  hpEmpty: 0x3a2a2a,
  sword: 0xeae3c8,
  swordEdge: 0xffffff,
  tower: 0x3d2e26,
  towerAccent: 0x8b6a4f,
  scroll: 0xc8b48a,
  ink: 0x2a1f18,
  inventoryFrame: 0x2a1f18,
  inventoryEmpty: 0x140d12,
  skillLocked: 0x3a2a2a,
  skillUnlocked: 0xe2dccd,
  bannerBg: 0x0a0510,
  bannerText: 0xf2c14e,
  /** Chalk/paint hue used for tutorial wall-arrow graffiti (off-white, hint of blue). */
  wallPaint: 0xddddee,
  heart: 0xff4d6d,
  heartHilite: 0xffb3c0,
  crystal: 0x7dd9f5,
  crystalHilite: 0xc4ffff,
  boot: 0x6b4423,
  bootHilite: 0xa17555,
  /** Escape feather — pale blue plume with a brighter quill highlight.
   *  Consumable powerup; pressing U pops the hero up one level. */
  feather: 0xa7d5ff,
  featherHilite: 0xffffff,
  featherShaft: 0x4d6a8a,
  /** Bird-Gegner silhouette (placeholder when bat sheet isn't loaded). */
  bird: 0x2a1f18,
  birdHilite: 0x6e5c4f,
  /** Lever (puzzle prop): wood post + brass handle. */
  leverPost: 0x3d2e26,
  leverHandle: 0xc1862e,
  leverHandleArmed: 0x6fd874,
  /** Swamp biome accents — murky green water + algae highlights. */
  swampDark: 0x1f2f1a,
  swampLight: 0x4a6a3b,
  swampMist: 0x6d8d59,
  /** Canopy vine — woody brown braid, deep moss-green leaves, lighter hilite. */
  vineRope: 0x4a3520,
  vineRopeHilite: 0x6b4a2a,
  vineLeaf: 0x3c5a3a,
  vineLeafHilite: 0x6a8a52,
  /** Parchment map screen — warm cream + brown ink. */
  parchment: 0xd8c79a,
  parchmentShadow: 0xa48d62,
  parchmentInk: 0x3a2a17,
  /** Shop room: warm stone wall + cool floor + lantern accents. */
  shopWall: 0x2c2026,
  shopWallHi: 0x4a3640,
  shopFloor: 0x1a1218,
  shopFloorEdge: 0x352630,
  shopLantern: 0xf2c14e,
  shopLanternGlow: 0xffe28a,
  /** Trade-card parchment + tier strip colors. */
  shopCardBg: 0xd8c79a,
  shopCardEdge: 0x6b4a2a,
  shopTierOwn: 0x6fd874,
  shopTierProjected: 0x7dd9f5,
  shopTierOut: 0x4a4047,
  /** Author badge parchment chip. */
  authorBadgeBg: 0x2a1f18,
  authorBadgeText: 0xe2dccd,
  /** Sidequest Author editor surfaces (Variant B). */
  authorPanelBg: 0xd8c79a,
  authorPanelEdge: 0x6b4a2a,
  authorChipBg: 0x2a1f18,
  authorChipBgFocus: 0x3a2a17,
  authorChipBgToggled: 0x3c5a3a,
  authorChevron: 0x6b4a2a,
  authorCursor: 0xe2dccd,
  authorDisabled: 0x6e5c4f,
  /** Pause-menu button parchment chip. */
  pauseButtonBg: 0xd8c79a,
  pauseButtonEdge: 0x6b4a2a,
  pauseButtonText: 0x2a1f18,
} as const;

/**
 * Gameplay tuning. Numbers come from ui-sketches.md + PRD §5. They live here
 * so we can pull a balance pass into one place rather than scene constructors.
 */
export const GAMEPLAY = {
  /** Hero stays at a fixed screen x; world scrolls (auto-run, PRD §5). */
  heroX: 96,
  /** World scroll speed in px/sec at native scale. */
  baseScrollSpeed: 90,
  /** Gravity in px/sec^2. Tuned for ~3-tile jump apex. */
  gravity: 900,
  /** Jump impulse in px/sec. */
  jumpVelocity: 320,
  /** Air-jump (double-jump) impulse in px/sec — slightly weaker than the ground jump. */
  doubleJumpVelocity: 280,
  /** Grace window after leaving a ledge during which a jump still counts as grounded (PRD §9 feel). */
  coyoteTimeMs: 110,
  /** Buffer window before landing during which a jump press still fires on landing. */
  jumpBufferMs: 120,
  /** Variable-height jump: holding shorter than this slices upward velocity. */
  jumpCutMultiplier: 0.45,
  /** Attack swing duration; matches the 24-fps Knight attack animation (22 frames / 24 fps ≈ 920 ms). */
  attackDurationMs: 920,
  /** Attack reach forward of the hero in px (sword sprite spawn offset). */
  attackReachPx: 22,
  /** Cooldown between successive attacks. Slightly longer than the swing so each attack reads as a discrete strike. */
  attackCooldownMs: 980,
  /** Hero hit points. Sketch shows 5/7 filled; treat 7 as full. */
  heroMaxHp: 7,
  /** Invulnerability after a hit (flicker window). */
  hitInvulnMs: 700,
  /** Seconds between Gegner spawns in Variant A. */
  gegnerSpawnIntervalSec: 6,
  /** Seconds between geo pickups. */
  geoSpawnIntervalSec: 2,
  /** Seconds between sidequest-tower placements on the ground. */
  towerSpawnIntervalSec: 14,
  /** Seconds between collectible "scroll" objects (inventory pickup). */
  scrollSpawnIntervalSec: 11,
  /** Geo threshold that unlocks double-jump. */
  doubleJumpUnlockGeo: 5,
  /** Kills threshold that unlocks the attack skill icon highlight (sword is always usable; this just flags "mastered"). */
  attackMasteredKills: 3,
  /** Inventory slot count visible on the HUD. */
  inventorySlots: 3,
  /** Banner on-screen duration. */
  bannerDurationMs: 2200,
  /** Seconds between Heart pickups (HP +1, capped at heroMaxHp). */
  heartSpawnIntervalSec: 22,
  /** Seconds between Crystal pickups (Geo +crystalGeoValue). */
  crystalSpawnIntervalSec: 17,
  /** Seconds between Boot pickups (speed boost). */
  bootSpawnIntervalSec: 26,
  /** Seconds between Escape-Feather pickups. Rare; this is a powerful
   *  consumable that lets the player bail out of any sub-level. */
  featherSpawnIntervalSec: 34,
  /** Cap on stored escape charges. Above this, additional feathers
   *  picked up are ignored (counted in telemetry but the player
   *  shouldn't be stockpiling). */
  featherMaxCharges: 3,
  /** Geo value granted by a Crystal pickup. */
  crystalGeoValue: 5,
  /** HP restored by a Heart pickup. */
  heartHpRestore: 1,
  /** Speed boost duration on Boot pickup. */
  speedBoostDurationSec: 4,
  /** Multiplier applied to world scroll speed while a Boot boost is active. */
  speedBoostMultiplier: 1.55,
  /** Duration of a sidequest sub-level in seconds. After this, a black
   *  end-gradient spawns at the right edge of the world; the player
   *  runs into it and the sub-level auto-pops back to the parent. */
  subLevelDurationSec: 20,
  /** Blink-in freeze on sub-level entry: world is paused and the hero
   *  flashes its alpha so the dive reads as a "materialising into the
   *  new world" beat instead of an instant jump-cut. */
  subLevelEntryBlinkMs: 750,
  /** Slow-start ramp after the entry freeze ends — scroll speed eases
   *  from `subLevelEntryStartSpeedMul` back up to 1.0 over this window. */
  subLevelEntrySlowRampMs: 500,
  subLevelEntryStartSpeedMul: 0.25,
  /** Tutorial duration (PRD §5 step 2). */
  tutorialDurationSec: 60,
  /** Full session duration excluding tutorial (PRD §5 step 3). */
  mainLoopDurationSec: 540,

  /**
   * Screen-shake budget (Visual Brief §7). Participants may be running
   * the build inside a moving car / moving simulator — large camera
   * jolts trigger motion sickness. We cap any shake to ≤2 px amplitude
   * and ≤80 ms duration; the `RunnerScene.shakeCamera()` helper enforces
   * the cap so individual call sites can request whatever they like
   * without breaking the budget.
   */
  shakeMaxAmplitudePx: 2,
  shakeMaxDurationMs: 80,

  /** Swamp segments slow scroll AND jump while the hero is inside one. */
  swampSpeedMultiplier: 0.55,
  swampJumpMultiplier: 0.7,
  /** Seconds between bird-Gegner spawns (independent of ground-Gegner cadence). */
  birdSpawnIntervalSec: 9,
  /** Sine-wave amplitude (px) for bird vertical motion. */
  birdAmplitudePx: 14,
  /** Sine cycles per second for bird motion. */
  birdFrequencyHz: 0.6,
  /** Bird flight height above the ground (px), tuned just above hero reach. */
  birdBaseHeightPx: 78,
  /** Seconds between dive-bomb projectile drops per bird. */
  birdDiveBombIntervalSec: 3.5,
  /** Falling speed of a dive-bomb projectile (px/sec). */
  birdProjectileFallSpeed: 220,

  /** Seconds between platform spawns. */
  platformSpawnIntervalSec: 5,
  /** Chance a freshly spawned platform oscillates (otherwise static). */
  movingPlatformChance: 0.4,
  /** Oscillation period (sec) for moving platforms. */
  movingPlatformPeriodSec: 2,
  /** Horizontal swing amplitude (px) for horizontal moving platforms. */
  movingPlatformAmplitudeX: 24,
  /** Vertical swing amplitude (px) for vertical moving platforms. */
  movingPlatformAmplitudeY: 10,

  /** Chance a sub-level rolls a puzzle objective (otherwise free exit). */
  puzzleChance: 0.6,
  /** Goal target: defeat-N-gegner puzzle. */
  puzzleGoalKills: 3,
  /** Goal target: collect-N-crystals puzzle. */
  puzzleGoalCrystals: 2,

  /**
   * Shop offer stream (Variant B). New offer arrives at uniform random
   * delay in [shopOfferIntervalMinSec, shopOfferIntervalMaxSec]; buffer
   * caps at shopBufferMax with oldest dropping.
   */
  shopOfferIntervalMinSec: 10,
  shopOfferIntervalMaxSec: 20,
  shopBufferMax: 20,
  /** How many cards the trade board renders at once (= number of vertical platforms). */
  shopBoardVisibleCards: 3,
  /** Free-walk velocity inside the shop room (px/sec). */
  shopWalkVelocity: 60,
  /** Forecast window for the "projected within N seconds" affordability tier. */
  shopForecastSeconds: 60,
  /** RunnerWallet income tracker: bucket size + buffer length (seconds). */
  walletForecastBucketMs: 1000,
  walletForecastWindowSec: 30,

  /**
   * Tree-canopy detour (forest biomes). Rare clusters spawn an ascent
   * vine, a wide invisible canopy platform near the top of the viewport,
   * a dense pickup band, and a descent vine at the far end. The hero
   * grabs the vine by jumping into it + pressing interact (Y/K) or
   * holding Up while airborne; while climbing the world scroll briefly
   * freezes so the ascent doesn't drift the vine out from under the
   * hero. Camera never moves vertically — the canopy sits inside the
   * static viewport (Visual Brief §1: horizontal-only camera budget).
   */
  /** Top y of the invisible canopy platform (origin top-left). Hero
   *  feet land here when deposited from the climb. Tuned to sit on
   *  the visible tree-crown line of the depth-0 Mysterious Forest
   *  parallax — lower (higher y) than the highest leaves so the hero
   *  reads as standing *on* the canopy, not floating above it. */
  canopyTopY: 52,
  /** Visual vine height in px — covers the full distance from the
   *  canopy top down to the ground line, with a few extra pixels at
   *  the top for the anchor leaf cluster. */
  canopyVineHeightPx: 148,
  /** Display width of a vine sprite. */
  canopyVineWidthPx: 12,
  /** Horizontal half-width within which a jumping hero can grab a vine. */
  canopyGrabReachPx: 14,
  /** Total horizontal length of an invisible canopy platform between
   *  ascent and descent vines. About one viewport wide so the player
   *  has time to collect pickups before running off the end. */
  canopyClusterWidthPx: 380,
  /** Climb duration (ms) — hero ascends/descends along the vine in
   *  this many ms. World scroll is frozen for the full duration so the
   *  vine stays under the hero. */
  canopyClimbDurationMs: 600,
  /** Cadence (seconds between vine clusters) in the depth-0 Mysterious
   *  Forest pack. Rare — the canopy detour is a forest-only treat, and
   *  Forrest / Snowy sub-levels skip it entirely. */
  canopyClusterIntervalSecMain: 65,
  /** One-shot tutorial spawn time (seconds into the tutorial). Plays a
   *  single cluster around the middle of the 60-s tutorial so the
   *  player meets the climb mechanic during the structured teaching
   *  run rather than discovering it cold in the main loop. */
  canopyClusterTutorialAtSec: 30,

  /** Sidequest Author editor (Variant B). Cost per save in scrolls. */
  sidequestAuthorScrollCost: 1,
  /** Horizontal offset (px) of an authored tower spawn from the hero's
   *  world x — biased forward so the player sees the portal appear and
   *  can run into it (auto-runner can't backtrack). */
  authoredTowerSpawnOffsetX: 48,
  /** Max forward lookahead (px) when searching for a safe spawn x that
   *  avoids the current abyss / swamp segment. */
  authoredTowerSpawnLookaheadX: 256,
  /** Step size (px) used when scanning for a safe spawn x. */
  authoredTowerSpawnStepX: 16,
  /** Vertical offset (px) of the AuthorTowerOverlay above the tower top. */
  authorTowerOverlayOffsetY: 24,
  /** Vertical bob amplitude (px) of the AuthorTowerOverlay. */
  authorTowerOverlayBobPx: 2,
  /** Bob period (ms) of the AuthorTowerOverlay. */
  authorTowerOverlayBobMs: 1400,
} as const;

/** Variant flag (PRD §3). Driven by Launcher selection at runtime. */
export type Variant = "A" | "B";

/**
 * Default state of the master debug toggle at app start. When `true`,
 * RunnerScene exposes developer shortcuts: number keys 0-9 jump directly
 * to that sub-level depth, the right arrow triple-speeds the world
 * scroll. Off in participant builds (PRD §10 — never expose debug
 * affordances during study runs).
 *
 * The default is wired to the `VITE_DEBUG_MODE` env variable so a clean
 * `git clone` of the repo defaults to OFF (participant build), while a
 * developer can flip it on locally by creating a gitignored
 * `game/v1/.env.local` with `VITE_DEBUG_MODE=true`. A checkbox in the
 * Launcher view overrides the default at runtime (the chosen state
 * rides on `SessionConfig.debugMode` into the runner scene).
 */
function readDebugDefaultFromEnv(): boolean {
  try {
    const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
    const raw = env?.VITE_DEBUG_MODE;
    return raw === "true" || raw === "1";
  } catch {
    return false;
  }
}
export const DEFAULT_DEBUG_MODE: boolean = readDebugDefaultFromEnv();
/** Fast-forward multiplier applied to world scroll while right arrow is held in debug mode. */
export const DEBUG_FAST_FORWARD = 3.0;

/** A curated background preset. Variant B can pick one before a run (PRD §6 — Background Upload / Cosmetic). */
export interface BackgroundPreset {
  id: string;
  /** Human-readable label shown in the picker. */
  label: string;
  /** Override palette slots used by Parallax. */
  skyFar: number;
  sky: number;
  ruinsMid: number;
  /** Detail-rectangle color riding the mid ruins band. */
  ruinsAccent: number;
}

/**
 * Curated palette slots for the Background Upload UGC feature (PRD §6).
 * The first entry is the "no UGC" default — matches the baseline PALETTE so
 * Variant A and an un-customised Variant B render identically.
 */
export const BACKGROUND_PRESETS: readonly BackgroundPreset[] = [
  {
    id: "default",
    label: "Default",
    skyFar: PALETTE.skyFar,
    sky: PALETTE.sky,
    ruinsMid: PALETTE.ruinsMid,
    ruinsAccent: PALETTE.sky,
  },
  {
    id: "rain",
    label: "City of Rain",
    skyFar: 0x1c2735,
    sky: 0x2a3a4d,
    ruinsMid: 0x3c4a5d,
    ruinsAccent: 0x1a242f,
  },
  {
    id: "verdant",
    label: "Verdant Halls",
    skyFar: 0x162a1f,
    sky: 0x223d2d,
    ruinsMid: 0x365440,
    ruinsAccent: 0x122019,
  },
  {
    id: "ember",
    label: "Ember Reach",
    skyFar: 0x2a1410,
    sky: 0x4a221a,
    ruinsMid: 0x6b3a26,
    ruinsAccent: 0x21100c,
  },
] as const;

export const DEFAULT_BACKGROUND_ID = BACKGROUND_PRESETS[0]!.id;

export function backgroundPresetById(id: string | undefined): BackgroundPreset {
  return BACKGROUND_PRESETS.find((p) => p.id === id) ?? BACKGROUND_PRESETS[0]!;
}

/** Hero skills tracked on the HUD. */
export type Skill = "jump" | "doubleJump" | "attack" | "interact";

/** Inventory item flavors collectable in v0.3 (decorative; informs banner copy). */
export type InventoryItemKind = "scroll" | "gem";
